/**
 * New (1.5.1: the type first — an event, or a workshop; then one summary card and one button, Save).
 * A workshop: who runs it (the FORA tech, staff, a volunteer), day and time, place, details, the cap (first come up to
 * it); Invite all tenants is on its card. An event (the reference's sheet): name, date, time, where (in the building — the room; off-site — the place
 * with the walking-directions link, like an outing), description, how spots are given (Everyone who RSVPs up to
 * the room · First come, first served up to N · Raffle for N tickets, max M per household), the audience
 * (tenants; volunteers: Event Support or all), a group outing (VISIONS CE on check-in), when RSVPs close.
 *
 * 1.7.4 — the numbers: Guests allowed (the Settings default unless chosen: 0 / 1 / 2; a raffle is one ticket per
 * tenant, so the choice is shown switched off), Volunteer places (optional — empty = no volunteers needed, 17 Oct 2026), and for a raffle
 * Tenant places (how many are drawn) and the Household extra ticket. The per-household limit is for the other two
 * rules only.
 *
 * 1.7.5 — "Food or items served": a switch, and when it is on "What we serve" (optional, printed on the delivery list
 * as what to bring). The RSVP of such an event asks each tenant whether they will come down or want it brought up.
 *
 * 1.7.7 — the calendar (D-1.7.7-1..3): Kind and Place from the two lists of Settings (Place has Other… for a typed
 * one), What to bring, the Accessibility line, RSVP on / off (off: the spots, the audience, the guests and the RSVP
 * close are not asked — the event is on the calendar only, sent as `everyone` with no capacity), Volunteers needed
 * (the 1.7.4 "Volunteer places" number under its one name here — sent as `volunteersNeeded`), Repeat with its end
 * date and the count before the press, and Reminders for this event. The save is the press that schedules the
 * reminders (D-1.7.7-0): the summary says how many before it is pressed. A Repeat's answer (`event.series`) is shown.
 * A long Repeat is made a few events a request (SERIES_PART): while `series.remaining` is above 0 the same input is
 * sent again with `seriesContinue: series.next`; the sheet says how far it is ("4 of 13 made…"), and when a request
 * fails it keeps what was made and offers Continue, which resumes from the last `next`.
 *
 * 1.7.9 — three steps (D-1.7.9-7, D-1.7.9-1). 1. What kind of event? — on the calendar only · people answer (RSVP) ·
 * a workshop, and a tick Needs volunteers. 2. Only the fields that kind needs (`eventFields` of shared/event-cards —
 * the same function as the cards; the RSVP switch is gone, the kind is the switch, and `kindDefaults` fixes what the
 * hidden fields would have sent), then the summary and Save: the event is saved here. 3. Tell people
 * (./TellPeople179): the announcement's draft to edit, Preview, Send — or Later. It never blocks the save. A workshop
 * keeps its own short form and ends at its save. Repeat gains "every month on the last Thursday" and "every 2, 3 or
 * 4 weeks", has no "5th", and says what it makes in words before the press.
 *
 * 1.8.2 (D-1.8.2-4): opened from Events → New with a kind already chosen in Elijah's words (`preset`, from
 * shared/new-kinds.ts), the sheet starts on step 2 — the kind's fields — with that kind, Repeat set to monthly for a
 * Monthly offering (the nth weekday of the day, or the last where the day is a 5th, until Repeat is changed by hand)
 * and the raffle chosen for a Raffle; Back from step 2 goes back to Events → New's "What kind of event?"
 * (`onBackToKind`). The rest is the 1.7.9 flow unchanged.
 */
import { useEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { AUDITORIUM_CAPACITY, type EventInput, type EventView, type SpotRule } from '@shared/events';
import { DEFAULT_REMINDER_CHOICE, EVENT_KINDS_DEFAULT, EVENT_REMINDER_TIMES_DEFAULT, EVENT_ROOMS_DEFAULT, MAX_SERIES, REPEAT_WEEKS, effectiveReminders, nthOfMonth, parseList, parseReminderTimes, repeatChoices, repeatDays, repeatSummary, repeatWords, type CalendarSettings, type ReminderChoice, type RepeatEvery, type SeriesResult } from '@shared/event-calendar';
import { eventFields, kindDefaults, type EventField, type EventKindChoice } from '@shared/event-cards';
import { monthlyRepeatFor, type NewEventPreset } from '@shared/new-kinds';
import { defaultCountMode } from '@shared/event-household-1026';
import { WHEELCHAIR_MAX, wheelchairSpotsOf } from '@shared/event-wheelchair-1025';
import { dayOfWeek, serviceMinutesNow } from '@shared/dates';
import { minutesLabel } from '@shared/volunteers';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/use-toast';
import { errorText } from '@/features/people/api';
import { SelectField, SwitchField, TextArea, TextField } from '@/features/people/fields';
import { DestinationPicker } from '@/features/volunteers/RequestSheet';
import { createEventReq, useEventRefresh, useEvents } from './api';
import { CountChoiceFields, KindChoiceFields, ReminderChoiceFields, RoomField, SelectT, TextT, firstRoom, reminderWords, remindersToSchedule } from './CalendarFields';
import { SpanishWording, useSpanishPeople } from '@/features/messages/SpanishWording';
import { useTellPeople, type ToldEvent } from './TellPeople179';
import { StepHead, SummaryCard } from '@/components/Steps';
import { apiFetch } from '@/lib/api';
import { formatDayMedium } from '@/lib/dates';
import { WORKSHOP_RUNNERS, type WorkshopInput, type WorkshopRunner } from '@shared/activities';

const TIMES = Array.from({ length: 31 }, (_, i) => 8 * 60 + i * 30); // 8:00 am – 11:00 pm
const iso = (k: string) => `${k.slice(0, 4)}-${k.slice(4, 6)}-${k.slice(6, 8)}`;
const num = (s: string) => Math.max(0, Math.floor(Number(s.replace(/\D/g, '')) || 0));
const isIso = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);

/** The calendar's lists while Settings is loading (the defaults of shared/event-calendar.ts). */
export const CALENDAR_FALLBACK: CalendarSettings = {
  kinds: parseList('', EVENT_KINDS_DEFAULT),
  rooms: parseList('', EVENT_ROOMS_DEFAULT),
  reminders: { on: true, times: parseReminderTimes(EVENT_REMINDER_TIMES_DEFAULT), audience: 'all' },
};

/** A Repeat being made: what the requests so far made and did not, how many days remain and where to go on from. */
interface SeriesRun {
  name: string;
  /** The input as it was saved — sent again, with `seriesContinue`, for each further part. */
  body: EventInput;
  created: SeriesResult['created'];
  skipped: SeriesResult['skipped'];
  total: number;
  remaining: number;
  next: SeriesResult['next'];
  running: boolean;
  /** Why a request failed (the server's words); Continue resumes from `next`. */
  failed: string | null;
  /** 1.7.9: the first event (step 3 announces it, and says it is the first only). */
  firstId: string;
  /** Every day was asked for: the sheet goes on to Tell people. */
  done?: boolean;
}

export function NewEventSheet({
  onClose,
  returnFocusTo,
  todayKey,
  onCreated,
  startDayKey,
  preset = null,
  onBackToKind,
}: {
  onClose: () => void;
  returnFocusTo: RefObject<HTMLElement | null>;
  todayKey: string;
  onCreated: (e: EventView) => void;
  /** 1.7.7: the day the sheet opens on (Month's "+"). */
  startDayKey?: string;
  /** 1.8.2: the kind chosen in Events → New ("Monthly offering", "Raffle" …): the sheet opens on step 2 with it. */
  preset?: NewEventPreset | 'workshop' | null;
  /** 1.8.2: Back from step 2 returns to Events → New's kind question instead of this sheet's own step 1. */
  onBackToKind?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useEventRefresh();
  const [name, setName] = useState('');
  const calendar = useEvents().data?.calendar ?? CALENDAR_FALLBACK;
  const [day, setDay] = useState(iso(startDayKey && /^\d{8}$/.test(startDayKey) ? startDayKey : todayKey));
  const [minutes, setMinutes] = useState(14 * 60);
  const [end, setEnd] = useState('');
  const kindPreset = preset && preset !== 'workshop' ? preset : null;
  // 25 Oct 2026: an Outing starts off-site, an In-house event in the building
  const [offSite, setOffSite] = useState(kindPreset?.offSite === true);
  // 1.7.7: null = not chosen yet — the list's own first room (the auditorium); Other… is a typed place
  const [roomPick, setRoomPick] = useState<string | null>(null);
  const [roomOther, setRoomOther] = useState(false);
  const room = roomPick ?? firstRoom(calendar.rooms);
  const [kind, setKind] = useState('');
  const [bring, setBring] = useState('');
  const [access, setAccess] = useState('');
  // D-1028-ONELANG-8: the Spanish wording beside the English, shown when people are marked Spanish
  const spanishPeople = useSpanishPeople();
  const [descriptionEs, setDescriptionEs] = useState('');
  const [bringEs, setBringEs] = useState('');
  const [accessEs, setAccessEs] = useState('');
  // 1.7.9 — step 1: the kind is asked, never assumed (null until chosen); the tick goes with either answer
  const [answer, setAnswer] = useState<EventKindChoice['answer'] | 'workshop' | null>(preset === 'workshop' ? 'workshop' : (kindPreset?.choice.answer ?? null));
  const [needsVolunteers, setNeedsVolunteers] = useState(!!kindPreset?.choice.volunteers);
  const [kindDone, setKindDone] = useState(!!preset);
  const type: 'event' | 'workshop' = answer === 'workshop' ? 'workshop' : 'event';
  const choice: EventKindChoice = { answer: answer === 'rsvp' ? 'rsvp' : 'calendar', volunteers: needsVolunteers && answer !== 'workshop' };
  const rsvp = choice.answer === 'rsvp';
  const fields = eventFields(choice);
  const has = (f: EventField) => fields.has(f);
  const [repeatPick, setRepeatPick] = useState<RepeatEvery>('none');
  // 1.8.2: a Monthly offering repeats monthly on the day chosen — until Repeat is changed by hand
  const [autoMonthly, setAutoMonthly] = useState(kindPreset?.repeat === 'monthly');
  const setRepeat = (r: RepeatEvery) => {
    setAutoMonthly(false);
    setRepeatPick(r);
  };
  const [weeks, setWeeks] = useState<number>(2);
  const [until, setUntil] = useState('');
  // 1.7.9 — step 3: the event that was saved (the announcement is about it)
  const [told, setTold] = useState<ToldEvent | null>(null);
  const [reminders, setReminders] = useState<ReminderChoice>(DEFAULT_REMINDER_CHOICE);
  const [series, setSeries] = useState<SeriesRun | null>(null);
  const seriesHead = useRef<HTMLHeadingElement>(null);
  const seriesShown = !!series;
  useEffect(() => {
    if (seriesShown) seriesHead.current?.focus();
  }, [seriesShown]);

  /** Press on while days remain: the same input with `seriesContinue`, adding up what was made and what was not. */
  const runSeries = async (start: SeriesRun) => {
    let run: SeriesRun = { ...start, running: start.remaining > 0 && !!start.next, failed: null };
    setSeries(run);
    while (run.remaining > 0 && run.next) {
      announce(t('cal177n.series.progress', { made: run.created.length, total: run.total }));
      try {
        const r = await createEventReq<{ series: SeriesResult }>({ ...run.body, seriesContinue: run.next });
        run = { ...run, created: [...run.created, ...r.series.created], skipped: [...run.skipped, ...r.series.skipped], total: r.series.total, remaining: r.series.remaining, next: r.series.next };
        setSeries(run);
      } catch (err) {
        const why = errorText(err);
        setSeries({ ...run, running: false, failed: why });
        refresh();
        return announce(t('cal177n.series.failed', { why, made: run.created.length, total: run.total }), 'assertive');
      }
    }
    setSeries({ ...run, running: false, done: true });
    refresh();
    const m = t('cal177n.series.made', { count: run.created.length });
    toast(m);
    announce(m);
  };
  const [place, setPlace] = useState<{
    name: string;
    address: string;
    approved: boolean;
  } | null>(null);
  const [description, setDescription] = useState('');
  // 25 Oct 2026 (D-1025-EVENTS): the kind decides how places are given — a Raffle draws them, anything else gives a place to everyone who says yes (up to the number, if there is one)
  const rule: SpotRule = kindPreset?.rule ?? 'everyone';
  const [capacity, setCapacity] = useState(String(AUDITORIUM_CAPACITY));
  // 22 Oct 2026 (D-1022-NOLIMIT): a new in-house event starts as "No limit" (everyone who says yes has a place); an outing (off-site) starts
  // with a number. Once the office presses the choice, it is theirs and stays.
  const [noLimitPressed, setNoLimitPressed] = useState<boolean | null>(null);
  const noLimit = noLimitPressed ?? (kindPreset ? kindPreset.noLimit : !offSite);
  const [tickets, setTickets] = useState('40');
  // 25 Oct 2026: wheelchair spots — an Outing starts with 2 (changeable in Edit); blank = this event does not count them
  const [wheelchair, setWheelchair] = useState(kindPreset?.wheelchairSpots === null || kindPreset?.wheelchairSpots === undefined ? '' : String(kindPreset.wheelchairSpots));
  // 26 Oct 2026 (D-1026-HOUSEHOLD): Outing → limited tickets (2); everything else → the whole household. Once pressed, it is the office's.
  const [countPressed, setCountPressed] = useState<'household' | 'limited' | null>(null);
  const [countN, setCountN] = useState('2');
  const countMode: 'household' | 'limited' = countPressed ?? defaultCountMode(kindPreset?.kind);
  const countAsked = rsvp && rule !== 'raffle' && kindPreset?.kind !== 'raffle';
  const [volunteers, setVolunteers] = useState(kindPreset?.choice.volunteers ? '1' : '0');
  const [tenants, setTenants] = useState(true);
  const [vols, setVols] = useState<'none' | 'eventSupport' | 'all'>('eventSupport');
  const [groupOuting, setGroupOuting] = useState(false);
  const [close, setClose] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [runBy, setRunBy] = useState<WorkshopRunner>('tech');
  const [runner, setRunner] = useState('');
  const [wsCap, setWsCap] = useState('12');
  const [wsPlace, setWsPlace] = useState('');
  const dayKey = day.replace(/-/g, '');
  const dayOk = /^\d{8}$/.test(dayKey);
  const untilKey = isIso(until) ? until.replace(/-/g, '') : null;
  // every day this save makes: the day itself, and with a Repeat each later one up to the end date
  // 1.7.9: what Repeat offers depends on the day (no "5th"; "the last" only on a last one) — a choice the day no longer allows is no repeat
  const offered = dayOk ? repeatChoices(dayKey) : (['none', 'weekly', 'everyN'] as RepeatEvery[]);
  const repeat: RepeatEvery = autoMonthly && dayOk ? monthlyRepeatFor(dayKey) : offered.includes(repeatPick) ? repeatPick : 'none';
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const repeatRule = { every: repeat, ...(repeat === 'everyN' ? { n: weeks } : {}), until: untilKey };
  const days = dayOk && repeat !== 'none' && untilKey ? repeatDays(dayKey, repeat, untilKey, weeks) : [dayKey];
  const lastDay = days[days.length - 1] ?? dayKey;
  const effective = effectiveReminders(calendar.reminders, reminders, rsvp);
  const toSchedule = dayOk ? remindersToSchedule(calendar.reminders, reminders, rsvp, days, minutes, { dayKey: todayKey, minutes: serviceMinutesNow() }) : 0;
  const armsWords = !calendar.reminders.on ? t('cal177n.rem.settingsOff') : reminders.mode === 'off' ? t('cal177n.rem.armsOff') : toSchedule > 0 ? t('cal177n.rem.arms', { count: toSchedule }) : t('cal177n.rem.armsNone');
  const repeatLabel = (r: RepeatEvery) => (r === 'everyN' ? t('ev179r.repeat.everyN') : repeatWords({ every: r }, dayKey, lang));
  const fifth = dayOk && nthOfMonth(dayKey) > 4;

  const problemOf = (): string | null => {
    if (!name.trim()) return t(type === 'workshop' ? 'workshops.needTitle' : 'events.new.needName');
    if (type === 'workshop') {
      if (!wsPlace.trim()) return t('events.new.needRoom');
      if (!num(wsCap)) return t('workshops.needCap');
      return null;
    }
    if (offSite && !place?.name) return t('events.new.needPlace');
    if (!offSite && !room.trim()) return t('events.new.needRoom');
    if (rsvp && rule === 'everyone' && !noLimit && !num(capacity)) return t('nolimit1022.needNumber');
    if (rsvp && rule === 'raffle' && !num(tickets)) return t('events.new.tickets');
    if (wheelchairSpotsOf(wheelchair) === undefined) return t('ev1025.wheelchair.bad', { max: WHEELCHAIR_MAX });
    if (has('audience') && !tenants && vols === 'none') return t('events.new.needAudience');
    if (has('volunteersNeeded') && num(volunteers) < 1) return t('ev179r.needVolunteers');
    if (repeat !== 'none' && !untilKey) return t('cal177n.repeat.needUntil');
    if (repeat !== 'none' && untilKey && untilKey < dayKey) return t('cal177n.repeat.untilBefore');
    return null;
  };
  const check = () => {
    // step 1: the kind is chosen before anything else is asked
    if (!kindDone) {
      const p = answer === null ? t('ev179r.kind.need') : null;
      setProblem(p);
      if (p) return announce(p, 'assertive');
      if (choice.volunteers && num(volunteers) < 1) setVolunteers('1');
      return setKindDone(true);
    }
    const p = problemOf();
    setProblem(p);
    if (p) return announce(p, 'assertive');
    setChecking(true);
  };

  const createWorkshop = async () => {
    const body: WorkshopInput = {
      title: name.trim(),
      runBy,
      runnerName: runner.trim(),
      dayKey,
      minutes,
      endMinutes: end ? Number(end) : null,
      place: wsPlace.trim(),
      details: description,
      detailsEs: descriptionEs.trim(),
      cap: num(wsCap),
    };
    setBusy(true);
    try {
      const e = await apiFetch<EventView>('/api/workshops/new', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const m = t('workshops.created', { name: e.name });
      toast(m);
      announce(m);
      refresh(e.eventId);
      onCreated(e);
      onClose();
    } catch (err) {
      const m = errorText(err);
      setProblem(m);
      setChecking(false);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    setProblem(null);
    if (type === 'workshop') return createWorkshop();
    const p = problemOf();
    if (p) return setProblem(p);
    const body: EventInput = {
      name: name.trim(),
      dayKey: day.replace(/-/g, ''),
      minutes,
      endMinutes: end ? Number(end) : null,
      room: offSite ? '' : room.trim(),
      offSite,
      place: offSite ? place : null,
      description,
      descriptionEs: descriptionEs.trim(),
      // 1.7.7: with no RSVP the event is on the calendar only — everyone, and the room's size is not asked
      rule: rsvp ? rule : 'everyone',
      capacity: rsvp && rule === 'everyone' && !noLimit ? num(capacity) || null : null,
      noLimit: rsvp && rule === 'everyone' && noLimit,
      tickets: !rsvp ? null : rule === 'raffle' ? num(tickets) || null : null,
      // 1.5.2: a per-household limit (default 2) — not in a raffle, which is one ticket per tenant (1.7.4)
      maxPerHousehold: rsvp && rule === 'raffle' ? null : 2,
      audience: rsvp ? { tenants, volunteers: vols } : { tenants: true, volunteers: 'none' },
      groupOuting: offSite && has('audience') && tenants && groupOuting,
      rsvpCloseDayKey: rsvp && close ? close.replace(/-/g, '') : null,
      // 1.7.4: null = the Settings default; a raffle asks about no guests whatever is kept here
      guestsAllowed: null,
      householdExtra: false,
      // 1.7.7 — the calendar. Volunteers needed is the one number (1.7.4's Volunteer places is not sent beside it)
      kind,
      bring: bring.trim(),
      accessLine: access.trim(),
      bringEs: bringEs.trim(),
      accessLineEs: accessEs.trim(),
      volunteersNeeded: Math.min(200, num(volunteers)),
      ...(repeat !== 'none' && untilKey ? { repeat: { every: repeat, ...(repeat === 'everyN' ? { n: weeks } : {}), until: untilKey } } : {}),
      reminders,
      // 25 Oct 2026: the numbers (guests, household extra) and Served are Edit's now; a new event starts from the Settings defaults
      served: false,
      servedWhat: '',
      ...(kindPreset ? { eventKind: kindPreset.kind } : {}),
      // 26 Oct 2026 (D-1026-HOUSEHOLD): how a household is counted — saved in the event's file exactly as Edit saves it
      ...(countAsked ? { countMode, ticketsPerHousehold: countMode === 'limited' ? Math.max(1, Math.min(20, num(countN) || 2)) : null } : {}),
      ...(rsvp && wheelchairSpotsOf(wheelchair) !== null && wheelchairSpotsOf(wheelchair) !== undefined ? { wheelchairSpots: wheelchairSpotsOf(wheelchair) } : {}),
      // 1.7.9: the kind fixes what its hidden fields would have sent (a calendar-only event is for tenants, takes everyone, serves nothing; no tick, no volunteers)
      ...kindDefaults(choice),
    };
    setBusy(true);
    try {
      const e = await createEventReq<EventView>(body);
      refresh(e.eventId);
      onCreated(e);
      if (e.series) {
        // a Repeat: the first events are made; the rest follow a few a request, then the count and each day not made
        setBusy(false);
        return void runSeries({ name: e.name, body, created: e.series.created, skipped: e.series.skipped, total: e.series.total ?? e.series.created.length + e.series.skipped.length, remaining: e.series.remaining ?? 0, next: e.series.next ?? null, running: false, failed: null, firstId: e.eventId });
      }
      // 1.7.9: the event is saved — the last step offers its announcement, and never blocks this save
      toast(t('ev179r.tell.saved', { name: e.name }));
      setChecking(false);
      setTold({ eventId: e.eventId, name: e.name, repeat: false, rsvp });
    } catch (err) {
      const m = errorText(err);
      setProblem(m);
      setChecking(false);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  const ruleWords = rule === 'everyone' ? (noLimit ? t('nolimit1022.rule') : t('events.rule.everyone', { n: num(capacity) || '—' })) : t('events.rule.raffle', { n: num(tickets) });
  const wheelchairN = wheelchairSpotsOf(wheelchair);
  const volunteersWords = num(volunteers) === 0 ? t('cal177n.sum.volunteersNone') : String(Math.min(200, num(volunteers)));
  const remindersWords = !calendar.reminders.on ? t('cal177n.rem.settingsOff') : !effective.on ? t('cal177n.rem.sumOff') : reminderWords(t, effective);
  const when = `${dayOk ? formatDayMedium(dayKey, i18n.language) : '—'} · ${minutesLabel(minutes)}${end ? `–${minutesLabel(Number(end))}` : ''}`;

  const tell = useTellPeople(told, onClose);
  const stepsOf = type === 'workshop' ? 2 : 3;

  return (
    <SheetContent
      title={t('events.new.title')}
      returnFocusTo={returnFocusTo}
      footer={
        told ? (
          tell.footer
        ) : series ? (
          <>
            <Button variant={series.failed ? 'secondary' : 'default'} disabled={series.running} onClick={() => (series.done ? setTold({ eventId: series.firstId, name: series.name, repeat: true, rsvp }) : onClose())} data-testid="ne-series-close">
              {series.done ? t('ev179r.tell.next') : t('cal177n.series.close')}
            </Button>
            {series.failed ? (
              <Button onClick={() => runSeries(series)} data-testid="ne-series-continue">
                {t('cal177n.series.continue')}
              </Button>
            ) : null}
          </>
        ) : checking ? (
          <>
            <Button variant="secondary" onClick={() => setChecking(false)} data-testid="event-back">
              {t('activities.back')}
            </Button>
            <Button disabled={busy} onClick={() => create()} data-testid="event-create">
              {type === 'workshop' ? t('workshops.create') : t('events.new.create')}
            </Button>
          </>
        ) : kindDone ? (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setProblem(null);
                if (onBackToKind) return onBackToKind();
                setKindDone(false);
              }}
              data-testid="ne-back-kind"
            >
              {t('activities.back')}
            </Button>
            <Button onClick={check} data-testid="event-next">
              {t('activities.next')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              {t('events.cancel')}
            </Button>
            <Button onClick={check} data-testid="event-next">
              {t('activities.next')}
            </Button>
          </>
        )
      }
    >
      {told ? (
        tell.body
      ) : series ? (
        <div className="card confirm-card cal177n-read" tabIndex={0} role="region" aria-label={t('cal177n.series.region')} data-testid="ne-series">
          <div className="card-b cal177n-series">
            <h3 ref={seriesHead} tabIndex={-1} className="step-q">
              {series.running ? t('cal177n.series.running') : series.failed ? t('cal177n.series.stopped') : t('cal177n.series.title')}
            </h3>
            <p className="cal177n-line" data-testid="ne-series-made">
              <Icon name={series.running || series.failed ? 'info' : 'check'} width={18} height={18} />
              <span>
                <b>{series.name}</b> — {series.running || series.failed ? t('cal177n.series.progress', { made: series.created.length, total: series.total }) : t('cal177n.series.made', { count: series.created.length })}
              </span>
            </p>
            {series.failed ? (
              <p className="field-err" role="alert" data-testid="ne-series-failed">
                <Icon name="alert" width={16} height={16} /> {t('cal177n.series.failed', { why: series.failed, made: series.created.length, total: series.total })}
              </p>
            ) : null}
            {series.skipped.length ? (
              <div data-testid="ne-series-skipped">
                <p className="cal177n-line">
                  <Icon name="alert" width={18} height={18} />
                  <span>{t('cal177n.series.skipped')}</span>
                </p>
                <ul>
                  {series.skipped.map((s) => (
                    <li key={s.dayKey}>
                      {formatDayMedium(s.dayKey, i18n.language)} — {s.why}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <p className="help">{t('cal177n.repeat.each')}</p>
          </div>
        </div>
      ) : checking ? (
        <div className="cal177n-read" tabIndex={0} role="region" aria-label={t('cal177n.sum.region')} data-testid="ne-summary">
          <SummaryCard
            testId="event-confirm"
            rows={
              type === 'workshop'
                ? [
                    {
                      label: t('workshops.c.what'),
                      value: `${t('workshops.typeWorkshop')} · ${name.trim()}`,
                    },
                    {
                      label: t('workshops.c.runBy'),
                      value: runner.trim() ? `${t(`workshops.run.${runBy}`)} (${runner.trim()})` : t(`workshops.run.${runBy}`),
                    },
                    { label: t('workshops.c.when'), value: when },
                    { label: t('workshops.c.where'), value: wsPlace.trim() },
                    { label: t('workshops.c.cap'), value: String(num(wsCap)) },
                    ...(description.trim()
                      ? [
                          {
                            label: t('workshops.c.details'),
                            value: description.trim(),
                          },
                        ]
                      : []),
                  ]
                : [
                    {
                      label: t('workshops.c.what'),
                      value: `${t('workshops.typeEvent')} · ${name.trim()}`,
                    },
                    ...(kind ? [{ label: t('cal177n.sum.kind'), value: kind }] : []),
                    { label: t('workshops.c.when'), value: when },
                    ...(repeat !== 'none' && untilKey ? [{ label: t('cal177n.repeat.sumLabel'), value: repeatSummary(repeatRule, dayKey, lang) }] : []),
                    {
                      label: t('workshops.c.where'),
                      value: offSite ? (place?.name ?? '') : room.trim(),
                    },
                    ...(kindPreset ? [{ label: t('ev1025.kind.label'), value: t(`ev1025.kind.${kindPreset.kind}`) }] : []),
                    ...(rsvp
                      ? [
                          { label: t('workshops.c.spots'), value: ruleWords },
                          ...(typeof wheelchairN === 'number' ? [{ label: t('ev1025.wheelchair.sum'), value: String(wheelchairN) }] : []),
                        ]
                      : [{ label: t('cal177n.sum.rsvp'), value: t('cal177n.sum.rsvpOff') }]),
                    ...(has('volunteersNeeded') ? [{ label: t('cal177n.volunteers'), value: volunteersWords }] : []),
                    ...(bring.trim() ? [{ label: t('cal177n.bring'), value: bring.trim() }] : []),
                    ...(access.trim() ? [{ label: t('cal177n.access'), value: access.trim() }] : []),
                    { label: t('cal177n.rem.sumLabel'), value: remindersWords },
                  ]
            }
          >
            {type === 'event' ? (
              <p className="cal177n-line cal177n-arms" data-testid="ne-arms">
                <Icon name={toSchedule > 0 ? 'info' : 'alert'} width={18} height={18} />
                <span>{armsWords}</span>
              </p>
            ) : null}
            <p className="help">{t('workshops.saveNote')}</p>
          </SummaryCard>
        </div>
      ) : !kindDone ? (
        <>
          <StepHead n={1} of={stepsOf} question={t('ev179r.kind.q')} id="ne-step-h" testId="ne-step-1" />
          <KindChoiceFields idPrefix="ne" answer={answer} volunteers={needsVolunteers} onAnswer={setAnswer} onVolunteers={setNeedsVolunteers} workshop labelledBy="ne-step-h" />
        </>
      ) : (
        <>
          <StepHead n={2} of={stepsOf} question={type === 'workshop' ? t('ev179r.step2.workshop') : t('ev179r.step2.q')} id="ne-step-h" testId="ne-step-2" />
          {type === 'event' ? (
            <p className="cal177n-line" data-testid="ne-kind-line">
              <Icon name="info" width={18} height={18} />
              <span>
                {t(`ev179r.kind.line.${choice.answer}`)}
                {choice.volunteers ? ` ${t('ev179r.kind.line.volunteers')}` : ''}
              </span>
            </p>
          ) : null}
          {type === 'workshop' ? (
            <>
              <TextField id="ws-title" label={t('workshops.title')} required value={name} onChange={setName} placeholder={t('workshops.titlePh')} />
              <fieldset className="field">
                <legend className="eyebrow">{t('workshops.runBy')}</legend>
                <div className="seg" role="group" aria-label={t('workshops.runBy')}>
                  {WORKSHOP_RUNNERS.map((r) => (
                    <button key={r} type="button" aria-pressed={runBy === r} onClick={() => setRunBy(r)} data-testid={`ws-run-${r}`}>
                      {t(`workshops.run.${r}`)}
                    </button>
                  ))}
                </div>
              </fieldset>
              <TextField id="ws-runner" label={t('workshops.runnerName')} value={runner} onChange={setRunner} />
              <div className="grid-2">
                <TextField id="ws-date" label={t('events.new.date')} type="date" required value={day} onChange={setDay} />
                <SelectField
                  id="ws-time"
                  label={t('events.new.time')}
                  value={String(minutes)}
                  onChange={(v) => setMinutes(Number(v))}
                  options={TIMES.map((m) => ({
                    value: String(m),
                    label: minutesLabel(m),
                  }))}
                />
              </div>
              <SelectField
                id="ws-end"
                label={t('events.new.end')}
                value={end}
                onChange={(v) => setEnd(v)}
                blank={t('events.new.noEnd')}
                options={TIMES.filter((m) => m > minutes).map((m) => ({
                  value: String(m),
                  label: minutesLabel(m),
                }))}
              />
              <TextField id="ws-place" label={t('events.new.room')} required value={wsPlace} onChange={setWsPlace} placeholder={t('workshops.placePh')} />
              <TextArea id="ws-details" label={t('workshops.details')} value={description} onChange={setDescription} help={t('events.new.descriptionHelp')} />
              <SpanishWording count={spanishPeople} value={descriptionEs} onChange={setDescriptionEs} rows={3} maxLength={2000} testId="ws-details-es" />
              <TextField id="ws-cap" label={t('workshops.cap')} required inputMode="numeric" value={wsCap} onChange={setWsCap} help={t('workshops.capHelp')} />
            </>
          ) : (
            <>
              <TextField id="ev-name" label={t('events.new.name')} required value={name} onChange={setName} placeholder={t('events.new.namePh')} />
              <SelectT id="ne-kind" label={t('cal177n.kind')} value={kind} onChange={setKind} blank={t('cal177n.kindNone')} options={calendar.kinds.map((k) => ({ value: k, label: k }))} help={t('cal177n.kindHelp')} />
              <div className="grid-2">
                <TextField id="ev-date" label={t('events.new.date')} type="date" required value={day} onChange={setDay} />
                <SelectField
                  id="ev-time"
                  label={t('events.new.time')}
                  value={String(minutes)}
                  onChange={(v) => setMinutes(Number(v))}
                  options={TIMES.map((m) => ({
                    value: String(m),
                    label: minutesLabel(m),
                  }))}
                />
              </div>
              <SelectField
                id="ev-end"
                label={t('events.new.end')}
                value={end}
                onChange={(v) => setEnd(v)}
                blank={t('events.new.noEnd')}
                options={TIMES.filter((m) => m > minutes).map((m) => ({
                  value: String(m),
                  label: minutesLabel(m),
                }))}
              />
              <fieldset className="field">
                <legend className="eyebrow">{t('events.new.where')}</legend>
                <div className="seg" role="group" aria-label={t('events.new.where')}>
                  <button type="button" aria-pressed={!offSite} onClick={() => setOffSite(false)} data-testid="ev-inside">
                    {t('events.new.inBuilding')}
                  </button>
                  <button type="button" aria-pressed={offSite} onClick={() => setOffSite(true)} data-testid="ev-offsite">
                    {t('events.new.offSite')}
                  </button>
                </div>
              </fieldset>
              {offSite ? (
                <DestinationPicker value={place} onChange={setPlace} idPrefix="ev" required />
              ) : (
                <RoomField
                  id="ev-room"
                  testId="ne-room"
                  otherId="ne-room-other"
                  rooms={calendar.rooms}
                  value={room}
                  other={roomOther}
                  onChange={(v, o) => {
                    setRoomPick(v);
                    setRoomOther(o);
                  }}
                  required
                />
              )}
              <p className="help">{t('events.new.whereHelp')}</p>
              <TextArea id="ev-desc" label={t('events.new.description')} value={description} onChange={setDescription} help={t('events.new.descriptionHelp')} />
              <SpanishWording count={spanishPeople} value={descriptionEs} onChange={setDescriptionEs} rows={3} maxLength={2000} testId="ev-desc-es" />
              <TextT id="ne-bring" label={t('cal177n.bring')} value={bring} onChange={setBring} maxLength={300} help={t('cal177n.bringHelp')} />
              <SpanishWording count={spanishPeople} value={bringEs} onChange={setBringEs} testId="ne-bring-es" />
              <TextT id="ne-access" label={t('cal177n.access')} value={access} onChange={setAccess} maxLength={300} help={t('cal177n.accessHelp')} />
              <SpanishWording count={spanishPeople} value={accessEs} onChange={setAccessEs} testId="ne-access-es" />
              {/* 25 Oct 2026 (D-1025-EVENTS): the kind decides how places are given. The numbers (guests, household extra) and Served are Edit's. */}
              {has('rule') ? (
                <>
                  {rule === 'everyone' ? (
                    <>
                      <SwitchField id="ev-no-limit" label={t('nolimit1022.label')} checked={noLimit} onChange={setNoLimitPressed} help={t('nolimit1022.help')} />
                      {noLimit ? null : (
                        <TextField
                          id="ev-capacity"
                          label={t('events.new.capacity')}
                          required
                          inputMode="numeric"
                          value={capacity}
                          onChange={setCapacity}
                          help={t('events.new.capacityHelp', {
                            n: AUDITORIUM_CAPACITY,
                          })}
                        />
                      )}
                    </>
                  ) : null}
                  {rule === 'raffle' ? <TextField id="ev-tickets" label={t('events.new.tickets')} required inputMode="numeric" value={tickets} onChange={setTickets} help={t('ev174.new.ticketsHelp')} /> : null}
                  {rule === 'raffle' ? <p className="help">{t('events.spots.raffleS')}</p> : null}
                  {kindPreset?.kind === 'outing' || kindPreset?.kind === 'raffle' ? <TextT id="ne-wheelchair" label={t('ev1025.wheelchair.label')} inputMode="numeric" value={wheelchair} onChange={setWheelchair} maxLength={2} help={t('ev1025.wheelchair.help')} /> : null}
                  {countAsked ? <CountChoiceFields prefix="ne" mode={countMode} onMode={setCountPressed} tickets={countN} onTickets={setCountN} /> : null}
                  <p className="help" data-testid="ne-numbers-later">
                    {t('ev1025.numbersLater')}
                  </p>
                </>
              ) : null}
              {answer !== 'workshop' ? (
                <SwitchField
                  id="ne-needs-volunteers"
                  label={t('ev1025.volunteers.switch')}
                  checked={needsVolunteers}
                  onChange={(on) => {
                    setNeedsVolunteers(on);
                    if (on && num(volunteers) < 1) setVolunteers('1');
                  }}
                  help={t('ev1025.volunteers.switchHelp')}
                />
              ) : null}
              {has('volunteersNeeded') ? <TextT id="ne-volunteers" label={t('ev1025.volunteers.howMany')} required inputMode="numeric" value={volunteers} onChange={setVolunteers} maxLength={3} help={t('ev179r.volunteersHelp')} /> : null}
              {has('audience') ? (
                <fieldset className="field">
                  <legend className="eyebrow">{t('events.new.audience')}</legend>
                  <div className="chips dense" role="group" aria-label={t('events.new.audience')}>
                    <button type="button" className="chip multi" aria-pressed={tenants} onClick={() => setTenants(!tenants)} data-testid="ev-aud-tenants">
                      {t('events.new.audTenants')}
                    </button>
                    <button type="button" className="chip multi" aria-pressed={vols === 'eventSupport'} onClick={() => setVols(vols === 'eventSupport' ? 'none' : 'eventSupport')} data-testid="ev-aud-es">
                      {t('events.new.audEventSupport')}
                    </button>
                    <button type="button" className="chip multi" aria-pressed={vols === 'all'} onClick={() => setVols(vols === 'all' ? 'none' : 'all')} data-testid="ev-aud-all">
                      {t('events.new.audAllVols')}
                    </button>
                  </div>
                </fieldset>
              ) : null}
              {offSite && has('audience') && tenants ? <SwitchField id="ev-outing" label={t('events.new.groupOuting')} checked={groupOuting} onChange={setGroupOuting} help={t('events.new.groupOutingHelp')} /> : null}
              {has('rsvpClose') ? <TextField id="ev-close" label={t('events.new.close')} type="date" value={close} onChange={setClose} help={t('events.new.closeHelp')} /> : null}
              <SelectT id="ne-repeat" label={t('cal177n.repeat.label')} value={repeat} onChange={(v) => setRepeat(v as RepeatEvery)} options={offered.map((r) => ({ value: r, label: repeatLabel(r) }))} help={fifth ? t('ev179r.repeat.fifth', { weekday: t(`cal177n.repeat.weekday.${dayOfWeek(dayKey)}`) }) : undefined} />
              {repeat === 'everyN' ? <SelectT id="ne-repeat-n" label={t('ev179r.repeat.weeksLabel')} value={String(weeks)} onChange={(v) => setWeeks(Number(v))} options={REPEAT_WEEKS.map((n) => ({ value: String(n), label: t(`ev179r.repeat.weeks.${n}`) }))} /> : null}
              {repeat !== 'none' ? (
                <>
                  <TextT id="ne-repeat-until" label={t('cal177n.repeat.until')} type="date" required value={until} onChange={setUntil} help={t('cal177n.repeat.untilHelp')} />
                  <p className="cal177n-line" role="status" data-testid="ne-repeat-count">
                    <Icon name={untilKey && untilKey >= dayKey ? 'info' : 'alert'} width={18} height={18} />
                    <span>{!dayOk || !untilKey ? t('cal177n.repeat.needUntil') : untilKey < dayKey ? t('cal177n.repeat.untilBefore') : `${repeatSummary(repeatRule, dayKey, lang)}. ${t('ev179r.repeat.last', { last: formatDayMedium(lastDay, i18n.language) })}${days.length >= MAX_SERIES ? ` ${t('cal177n.repeat.countMax', { max: MAX_SERIES })}` : ''} ${t('cal177n.repeat.each')}`}</span>
                  </p>
                </>
              ) : null}
              <ReminderChoiceFields value={reminders} onChange={setReminders} defaults={calendar.reminders} hasRsvp={rsvp} idPrefix="ne" />
              <p className="cal177n-line cal177n-arms" data-testid="ne-arms-form">
                <Icon name={toSchedule > 0 ? 'info' : 'alert'} width={18} height={18} />
                <span>{armsWords}</span>
              </p>
              <p className="help">{t('events.new.folder')}</p>
            </>
          )}
        </>
      )}
      {problem ? (
        <p className="field-err" role="alert" data-testid="event-problem">
          <Icon name="alert" width={16} height={16} /> {problem}
        </p>
      ) : null}
    </SheetContent>
  );
}
